import { Stack } from "@mantine/core";
import React, { useMemo } from "react";
import Plot from "react-plotly.js";
import { useAppColors } from "@/hooks/contextHooks/useAppColors";

const createWindLoadsGraph = (
  windLoadsData: any,
  isDark: boolean,
  colors: any,
) => {
  if (!windLoadsData || !windLoadsData.lut) {
    console.error("Wind loads data is missing");
    return null;
  }

  const angles = windLoadsData.lut.angles_deg;
  const cx = windLoadsData.lut.cx;
  const cy = windLoadsData.lut.cy;
  const ck = windLoadsData.lut.ck;
  const cn = windLoadsData.lut.cn;

  return {
    data: [
      {
        x: angles,
        y: cx,
        mode: "lines+markers",
        name: "cx",
        line: { shape: "spline", color: "blue" },
        marker: { symbol: "circle" },
      },
      {
        x: angles,
        y: cy,
        mode: "lines+markers",
        name: "cy",
        line: { shape: "spline", color: "red" },
        marker: { symbol: "circle" },
      },
      {
        x: angles,
        y: ck,
        mode: "lines+markers",
        name: "ck",
        line: { shape: "spline", color: "green" },
        marker: { symbol: "circle" },
      },
      {
        x: angles,
        y: cn,
        mode: "lines+markers",
        name: "cn",
        line: { shape: "spline", color: "yellow" },
        marker: { symbol: "circle" },
      },
    ],
    layout: {
      title: "Wind loads on hull and superstructures",
      xaxis: {
        title: "Apparent Wind Angle (°)",
        gridcolor: isDark ? "#555" : "#ccc",
      },
      yaxis: {
        title: "Non-dimensional Wind Coefficient",
        gridcolor: isDark ? "#555" : "#ccc",
      },
      legend: {
        title: { text: "Coefficients" },
        orientation: "h",
        yanchor: "top",
        y: -0.2,
        xanchor: "center",
        x: 0.5,
      },
      paper_bgcolor: colors.gray,
      plot_bgcolor: colors.gray,
      font: { color: colors.secondary },
      margin: { l: 60, r: 30, t: 50, b: 120 },
    },
  };
};

const createPropulsionGraph = (
  propulsionData: any,
  isDark: boolean,
  colors: any,
) => {
  if (!propulsionData?.propulsion_lib?.propellers?.main_screw?.lut) {
    console.error("Propulsion data is missing or in an unexpected format");
    return null;
  }

  const {
    j: jData,
    kt: ktData,
    kq: kqData,
  } = propulsionData.propulsion_lib.propellers.main_screw.lut;

  const efficiencyData = jData.map(
    (j: number, index: number) =>
      (j * ktData[index]) / (2 * Math.PI * kqData[index]) || 0,
  );

  return {
    data: [
      { x: jData, y: ktData, name: "KT", line: { color: "red" } },
      {
        x: jData,
        y: kqData.map((kq: number) => 10 * kq),
        name: "10KQ",
        line: { color: "green", dash: "dash" },
      },
      {
        x: jData,
        y: efficiencyData,
        name: "ETA0",
        line: { color: "blue", dash: "dot" },
      },
    ],
    layout: {
      title: "Propulsion: Open Water Curves",
      xaxis: { title: "J", gridcolor: isDark ? "#555" : "#ccc" },
      yaxis: { title: "KT, 10KQ, ETA0", gridcolor: isDark ? "#555" : "#ccc" },
      legend: {
        title: { text: "Coefficients" },
        orientation: "h",
        yanchor: "top",
        y: -0.2,
        xanchor: "center",
        x: 0.5,
      },
      paper_bgcolor: colors.gray,
      plot_bgcolor: colors.gray,
      font: { color: colors.secondary },
      margin: { l: 60, r: 30, t: 50, b: 200 },
    },
  };
};

interface PlotlyGraphsProps {
  data: Record<string, any>;
}

const PlotlyGraphs: React.FC<PlotlyGraphsProps> = ({ data }) => {
  const { colors, isDarkMode } = useAppColors();

  const graphs = useMemo(() => {
    if (!data) {
      return { summary: null, wind: null, propulsion: null };
    }

    const velocity = data.dtwin?.loads?.calm_water_resistance?.lut?.velocity_kt;

    //########## Temporary solution waiting for predator fix
    let resistance =
      data.dtwin?.loads?.calm_water_resistance?.lut?.resistance_kN ||
      data.dtwin?.loads?.calm_water_resistance?.lut?.resistance_N;
    if (data.dtwin?.loads?.calm_water_resistance?.lut?.resistance_N) {
      resistance = resistance.map((value: number) => value / 1000);
    }
    // ############""
    if (!velocity || !resistance) {
      console.error("Velocity or resistance data is missing");
      return { summary: null, wind: null, propulsion: null };
    }

    const posResistance = resistance.map((r: number) => Math.abs(r));

    const summaryFigure = {
      data: [
        {
          x: velocity,
          y: posResistance,
          mode: "lines+markers",
          name: "Calm Water Resistance Curve",
        },
      ],
      layout: {
        title: "Calm Water Resistance Curve",
        xaxis: {
          title: "Velocity (kt)",
          gridcolor: isDarkMode ? "#555" : "#ccc",
        },
        yaxis: {
          title: "Resistance (kN)",
          gridcolor: isDarkMode ? "#555" : "#ccc",
        },
        paper_bgcolor: colors.gray,
        plot_bgcolor: colors.gray,
        font: { color: colors.secondary },
        margin: { l: 60, r: 30, t: 50, b: 100 },
      },
    };

    const windFigure = createWindLoadsGraph(
      data.dtwin?.loads?.wind_loads,
      isDarkMode,
      colors,
    );
    const propulsionFigure = createPropulsionGraph(
      data.dtwin?.loads?.propulsion,
      isDarkMode,
      colors,
    );

    return {
      summary: summaryFigure,
      wind: windFigure,
      propulsion: propulsionFigure,
    };
  }, [data, isDarkMode, colors]);

  if (!graphs.summary && !graphs.wind && !graphs.propulsion) {
    return null;
  }

  return (
    <Stack style={{ width: "100%" }}>
      {graphs.summary && (
        <Plot
          data={graphs.summary.data}
          layout={{ ...graphs.summary.layout, autosize: true }}
          style={{ width: "100%", height: "450px" }}
          useResizeHandler
          config={{ displayModeBar: false }}
        />
      )}
      {graphs.wind && (
        <Plot
          data={graphs.wind.data}
          layout={{ ...graphs.wind.layout, autosize: true }}
          style={{ width: "100%", height: "450px" }}
          useResizeHandler
          config={{ displayModeBar: false }}
        />
      )}
      {graphs.propulsion && (
        <Plot
          data={graphs.propulsion.data}
          layout={{ ...graphs.propulsion.layout, autosize: true }}
          style={{ width: "100%", height: "450px" }}
          useResizeHandler
          config={{ displayModeBar: false }}
        />
      )}
    </Stack>
  );
};

export default PlotlyGraphs;
